import { supabase, run } from './db';
import { cached } from './local/cache';
import { INSUMOS_SUGERIDOS } from '../constants/insumos';

/**
 * Catálogo de insumos do laboratório e o estoque deles.
 *
 * O catálogo serve aos custos do serviço: escolher "Agulha de aspiração" numa
 * linha traz unidade, categoria e — quando houver — o preço. O estoque é
 * opcional, por insumo: só quem marca `controla_estoque` ganha saldo.
 *
 * A SAÍDA NÃO PASSA POR AQUI. Ela é escrita no banco pelo gatilho
 * `baixa_insumos`, a partir das linhas de custo gravadas no serviço — que viajam
 * pela fila offline com o resto do serviço. Daqui só saem entradas e ajustes,
 * que são administração de estoque feita com sinal, como no sêmen.
 */

/** Catálogo com saldo. Ativos primeiro, por nome. */
export const listInsumos = (laboratoryId) =>
  cached(`insumos:${laboratoryId}`, () => run(supabase
    .from('insumos_com_saldo')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('ativo', { ascending: false })
    .order('nome')));

export const insertInsumo = (row) =>
  run(supabase.from('insumos').insert([row]).select().single());

export const updateInsumo = (id, updates) =>
  run(supabase.from('insumos').update(updates).eq('id', id));

export const deleteInsumo = (id) =>
  run(supabase.from('insumos').delete().eq('id', id));

/**
 * Traz a lista sugerida para o catálogo, sem repetir o que já existe.
 *
 * Compara pelo nome sem caixa nem espaço, que é como o índice único do banco
 * compara — senão um "agulha de aspiração OPU 18G/20G" já cadastrado faria a
 * importação inteira falhar.
 */
export const sugeridosQueFaltam = (existentes = []) => {
  const tem = new Set(existentes.map(i => (i.nome || '').trim().toLowerCase()));
  return INSUMOS_SUGERIDOS.filter(s => !tem.has(s.nome.trim().toLowerCase()));
};

// `escolhidos`: os nomes marcados na tela. Sem ele, importa todos os que faltam.
export const importarSugeridos = (laboratoryId, existentes = [], escolhidos = null) => {
  const linhas = sugeridosQueFaltam(existentes)
    .filter(s => !escolhidos || escolhidos.includes(s.nome))
    .map(s => ({ ...s, laboratory_id: laboratoryId }));
  if (linhas.length === 0) return Promise.resolve([]);
  return run(supabase.from('insumos').insert(linhas).select());
};

/** Movimentos de um insumo, mais recentes primeiro. */
export const listMovimentos = (insumoId) =>
  run(supabase
    .from('insumo_movimentos')
    .select('*')
    .eq('insumo_id', insumoId)
    .order('data', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(50));

/** Entrada (compra) ou ajuste (inventário). */
export const lancarMovimento = (row) =>
  run(supabase.from('insumo_movimentos').insert([{ ...row, origem: 'manual' }]));

export const excluirMovimento = (id) =>
  run(supabase.from('insumo_movimentos').delete().eq('id', id));
